// desktopBridge — the typed, OPTIONAL window bridge the Electron preload
// exposes to the page as `window.bildaDesktop`.
//
// AUDIENCE NOTE (AI register, deliberately thorough): this file is the seam
// contract between TWO packages that share no build:
//
//   • packages/desktop (the Electron shell): its preload script calls
//     `contextBridge.exposeInMainWorld("bildaDesktop", …)` with an object
//     whose runtime shape MUST match `DesktopBridge` below. The desktop side
//     mirrors this contract by hand, exactly like the appCommand mirror
//     (packages/desktop/src/appCommand.cts) — when this type changes, the
//     preload implementation must change in the same commit.
//   • packages/client (this package): renderer code never touches
//     `window.bildaDesktop` directly. It calls `getDesktopBridge()` and
//     branches on `undefined`. `undefined` means "not running inside the
//     desktop shell" (plain browser / Storybook / tests) OR "the exposed
//     object is malformed" — both degrade to the web behaviour, never crash.
//
// CREDENTIAL DISCIPLINE (load-bearing): the API key is WRITE-ONLY from the
// renderer's point of view. `readApiKeyPresence` answers only "is a key
// configured?" — there is deliberately NO method that returns the key value
// to the page, so a compromised or merely buggy renderer can never exfiltrate
// it. The desktop main process owns the stored secret. Do not add a
// `readApiKey(): Promise<string>` method to this contract.
//
// PURITY: this module is a boundary reader, not a component. It performs no
// I/O itself — it narrows an untyped global (`window.bildaDesktop` arrives as
// `unknown`; the preload world is untyped wire, same trust level as an IPC
// payload) and re-wraps the exposed functions so the values crossing into the
// page are parsed, not cast. Impure callers (AppShellHost effects, the
// Settings surface's port wiring) invoke the returned methods.
//
// THIS-BINDING CONSTRAINT for the preload implementation: the exposed
// functions are re-wrapped and called unbound here, so they must not rely on
// `this` (contextBridge-exposed functions never should).

import { bool, fields, num, str } from "./wireShape.js";

// === Contract types ==========================================================

// Answer to "is an agent API key configured on the desktop side?".
// `configured: true` means a non-empty key is stored; it says NOTHING about
// the key's validity (an invalid key still surfaces later as the agent's
// AgentAuthMissing error on a turn).
export type ApiKeyPresence = { readonly configured: boolean };

// Acknowledgement of a settings mutation. `ok: false` means the desktop side
// could not persist the change (e.g. the config file was unwritable); the
// caller should surface a retryable failure, not assume success.
export type BridgeAck = { readonly ok: boolean };

// Where the local event log lives and how big it currently is.
// `eventsPath` is an absolute OS path (author-facing display + reveal
// affordance); `sizeBytes` is the current file size (0 when the file does
// not exist yet).
export type EventsFileInfo = {
    readonly eventsPath: string;
    readonly sizeBytes: number;
};

export type PlaybackStatus = {
    readonly position: number;
    readonly length: number;
    readonly historical: boolean;
};

// BYOK credential management, write-only from the renderer (see the header).
export type DesktopSettingsBridge = {
    // Presence check only — never the key value.
    readonly readApiKeyPresence: () => Promise<ApiKeyPresence>;
    // Store (or replace) the agent API key. The main process persists it and
    // the in-process server picks it up; the renderer never reads it back.
    readonly writeApiKey: (key: string) => Promise<BridgeAck>;
    // Remove the stored key entirely (distinct intent from writing an empty
    // string — its own method, never a flag).
    readonly clearApiKey: () => Promise<BridgeAck>;
};

// Local-data introspection: where the events file is, and a native
// reveal-in-file-manager affordance (Finder / Explorer / file manager).
export type DesktopDataBridge = {
    readonly info: () => Promise<EventsFileInfo>;
    // Opens the OS file manager with the events file selected. Fire-and-
    // observe: no result value; a failure is silent (the author simply sees
    // no window), which is acceptable for this affordance.
    readonly revealEventsFile: () => Promise<void>;
    readonly revealLastDownload: () => Promise<void>;
    // Opens (or raises) the local VCR debugger in its own desktop window.
    readonly openVcr: () => Promise<void>;
    // Opens (or raises) the dev-state Inspector in its own desktop window.
    // Dev-only: the main process wires it only when dev routes are on.
    readonly openInspector: () => Promise<void>;
    readonly playbackStatus: () => Promise<PlaybackStatus>;
};

// The OS family the shell runs on. Drives platform-register UI decisions
// (e.g. Kbd glyphs: "⌘" on mac, "Ctrl" on windows/linux) — derived ONCE in
// the main process from `process.platform`, never sniffed from the UA.
export type DesktopPlatform = "mac" | "windows" | "linux";

// The full bridge surface. Grouped by concern so each group can grow without
// the others noticing; every group is required (the preload exposes all of
// them or the whole bridge reads as absent).
export type DesktopBridge = {
    readonly settings: DesktopSettingsBridge;
    readonly data: DesktopDataBridge;
    readonly platform: DesktopPlatform;
};

// The window property the preload exposes the bridge under. The desktop
// preload must use this exact string in `exposeInMainWorld` (hand-mirrored,
// like the appCommand channel name).
export const DESKTOP_BRIDGE_PROPERTY = "bildaDesktop";

// === Boundary narrowing ======================================================
// Everything below turns the untyped `window.bildaDesktop` value into a
// `DesktopBridge` WITHOUT a single `as` cast: presence is checked with `in`
// (TS unlisted-property narrowing), objects are read through the wireShape
// readers, and each exposed function is re-wrapped so its RESULT is parsed
// into the contract shape (mismatched fields degrade to honest defaults:
// `configured`/`ok` read as false, sizes as 0, paths as "").

// The loosest callable shape a bridge function can have. The runtime check
// (`typeof v === "function"`) cannot verify a signature, so this predicate is
// the one place the contract trusts the preload to have exposed the right
// arity — the result-side parsing above it keeps that trust contained.
type BridgeFn = (...args: readonly unknown[]) => unknown;
const asBridgeFn = (v: unknown): BridgeFn | undefined => {
    const isFn = (x: unknown): x is BridgeFn => typeof x === "function";
    return isFn(v) ? v : undefined;
};

const readPlatform = (v: unknown): DesktopPlatform | undefined => {
    const p = str(v);
    return p === "mac" || p === "windows" || p === "linux" ? p : undefined;
};

// The raw exposed value, or undefined outside a browser context / when the
// preload exposed nothing. `window` is narrowed structurally: `in` on an
// `object` binding — no ambient global augmentation, no cast.
const bridgeCandidate = (): unknown => {
    if (typeof window === "undefined") return undefined;
    const w: object = window;
    return "bildaDesktop" in w ? w.bildaDesktop : undefined;
};

// Accessor the renderer calls. Returns the typed bridge when the desktop
// preload exposed a well-formed one, else `undefined` (= behave as the web
// shell). Cheap enough to call per use; callers may also capture it once at
// mount. Each returned method awaits the exposed function and parses its
// result into the contract shape — the page never sees a raw preload value.
export const getDesktopBridge = (): DesktopBridge | undefined => {
    const root = fields(bridgeCandidate());
    if (root === undefined) return undefined;

    const platform = readPlatform(root.get("platform"));
    const settings = fields(root.get("settings"));
    const data = fields(root.get("data"));
    if (platform === undefined || settings === undefined || data === undefined)
        return undefined;

    const readApiKeyPresence = asBridgeFn(settings.get("readApiKeyPresence"));
    const writeApiKey = asBridgeFn(settings.get("writeApiKey"));
    const clearApiKey = asBridgeFn(settings.get("clearApiKey"));
    const info = asBridgeFn(data.get("info"));
    const revealEventsFile = asBridgeFn(data.get("revealEventsFile"));
    const revealLastDownload = asBridgeFn(data.get("revealLastDownload"));
    const openVcr = asBridgeFn(data.get("openVcr"));
    const openInspector = asBridgeFn(data.get("openInspector"));
    const playbackStatus = asBridgeFn(data.get("playbackStatus"));
    if (
        readApiKeyPresence === undefined ||
        writeApiKey === undefined ||
        clearApiKey === undefined ||
        info === undefined ||
        revealEventsFile === undefined ||
        revealLastDownload === undefined ||
        openVcr === undefined ||
        openInspector === undefined ||
        playbackStatus === undefined
    )
        return undefined;

    const ackOf = async (raw: unknown): Promise<BridgeAck> => ({
        ok: bool(fields(await Promise.resolve(raw))?.get("ok")),
    });

    return {
        platform,
        settings: {
            readApiKeyPresence: async () => ({
                configured: bool(
                    fields(await Promise.resolve(readApiKeyPresence()))?.get(
                        "configured",
                    ),
                ),
            }),
            writeApiKey: (key) => ackOf(writeApiKey(key)),
            clearApiKey: () => ackOf(clearApiKey()),
        },
        data: {
            info: async () => {
                const raw = fields(await Promise.resolve(info()));
                return {
                    eventsPath: str(raw?.get("eventsPath")) ?? "",
                    sizeBytes: num(raw?.get("sizeBytes")) ?? 0,
                };
            },
            revealEventsFile: async () => {
                await Promise.resolve(revealEventsFile());
            },
            revealLastDownload: async () => {
                await Promise.resolve(revealLastDownload());
            },
            openVcr: async () => {
                await Promise.resolve(openVcr());
            },
            openInspector: async () => {
                await Promise.resolve(openInspector());
            },
            playbackStatus: async () => {
                const raw = fields(await Promise.resolve(playbackStatus()));
                const position = num(raw?.get("position")) ?? 0;
                const length = num(raw?.get("length")) ?? 0;
                return {
                    position,
                    length,
                    historical: bool(raw?.get("ok")) && position < length,
                };
            },
        },
    };
};
